import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { selectAdmin } from '../redux/adminSlice';
import DataTable from 'react-data-table-component';
import {
  Search,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  MoreVertical,
  Info,
  AlertCircle,
  Coffee,
  DollarSign,
  MapPin,
  Phone,
  ArrowRightCircle,
  TrendingUp,
  Package,
  ShoppingBag,
  Lock,
  CreditCard,
  Banknote
} from 'lucide-react';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// --- Sub-components ---

const StatsCard = ({ title, value, icon: Icon, color, subValue }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex items-center justify-between hover:shadow-md transition-shadow">
    <div>
      <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-gray-800">{value}</h3>
      {subValue && <p className="text-xs text-green-600 mt-1 font-medium">{subValue}</p>}
    </div>
    <div className={`p-3 rounded-full ${color.bg} ${color.text}`}>
      <Icon size={24} />
    </div>
  </div>
);

const MealDetailModal = ({ isOpen, onClose, orderDetails }) => {
  if (!isOpen || !orderDetails) return null;

  // Calculate total for package meals
  const calculatePackageTotal = () => {
    let total = 0;
    if (orderDetails.selectedMeals?.package?.selectedMeals) {
      orderDetails.selectedMeals.package.selectedMeals.forEach(dayMeal => {
        dayMeal.meals.forEach(meal => {
          total += meal.fareDetails?.totalFare || 0;
        });
      });
    }
    return total;
  };

  // Calculate total for addons
  const calculateAddonsTotal = () => {
    let total = 0;
    if (orderDetails.selectedMeals?.addons) {
      orderDetails.selectedMeals.addons.forEach(addon => {
        total += addon.fareDetails?.totalFare || 0;
      });
    }
    return total;
  };

  const packageTotal = calculatePackageTotal();
  const addonsTotal = calculateAddonsTotal();
  const grandTotal = packageTotal + addonsTotal;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col animate-fadeIn">

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
            <p className="text-sm text-gray-500">#{orderDetails.orderID}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"
          >
            <XCircle size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 space-y-6">

          {/* User Information Card */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wide mb-3 flex items-center">
              <MapPin size={16} className="mr-2" /> Customer & Delivery
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-blue-600 font-semibold">EMAIL</p>
                <p className="text-gray-800 font-medium">{orderDetails.userEmail}</p>
              </div>
              <div>
                <p className="text-xs text-blue-600 font-semibold">PHONE</p>
                <p className="text-gray-800 font-medium">{orderDetails.address?.phoneNumber || 'N/A'}</p>
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-xs text-blue-600 font-semibold">ADDRESS</p>
                <p className="text-gray-800">
                  {[
                    orderDetails.address?.flatNumber,
                    orderDetails.address?.buildingName,
                    orderDetails.address?.streetName,
                    orderDetails.address?.landmark,
                    orderDetails.address?.city
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
            </div>
            {/* Map Button */}
            <div className="mt-4 pt-3 border-t border-blue-200 flex justify-end">
              <button
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center shadow-sm"
                onClick={() => {
                  const lat = orderDetails.address?.latitude || 25.2048; // Default to Dubai if missing
                  const lng = orderDetails.address?.longitude || 55.2708;
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                }}
              >
                <MapPin size={14} className="mr-1.5" /> Open in Maps
              </button>
            </div>
          </div>

          {/* Package Info */}
          {orderDetails.selectedMeals?.package?.selectedPackage && (
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center mb-4">
                <Calendar size={18} className="text-orange-500 mr-2" />
                <h3 className="font-bold text-gray-800">Package Schedule</h3>
              </div>
              <div className="flex gap-8">
                <div>
                  <p className="text-xs text-gray-500">START DATE</p>
                  <p className="font-medium">{orderDetails.selectedMeals.package.startDate}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">END DATE</p>
                  <p className="font-medium">{orderDetails.selectedMeals.package.endDate}</p>
                </div>
              </div>
            </div>
          )}

          {/* Meals List */}
          {orderDetails.selectedMeals?.package?.selectedMeals?.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-800 mb-3 flex items-center">
                <Coffee size={18} className="text-green-600 mr-2" /> Selected Meals
              </h3>
              <div className="space-y-3">
                {orderDetails.selectedMeals.package.selectedMeals.map((dayMeals, index) => (
                  <div key={index} className="border border-gray-100 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-100 flex items-center">
                      <Calendar size={14} className="text-gray-400 mr-2" />
                      <span className="font-medium text-sm text-gray-700">{dayMeals.date}</span>
                    </div>
                    <div className="p-3 bg-white">
                      {dayMeals.meals?.map((meal, mIdx) => (
                        <div key={mIdx} className="flex justify-between items-start py-2 border-b last:border-0 border-dashed border-gray-100">
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{meal.mealName}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {meal.foodType && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide
                                                        ${meal.foodType === 'veg' ? 'bg-green-100 text-green-700' :
                                    meal.foodType === 'non-veg' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {meal.foodType}
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{meal.portionDetails?.portion} Portion(s)</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-gray-800">AED {meal.fareDetails?.totalFare}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Addons */}
          {orderDetails.selectedMeals?.addons?.length > 0 && (
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
              <h3 className="font-bold text-purple-900 mb-2 flex items-center">
                <Info size={16} className="mr-2" /> Add-ons
              </h3>
              {orderDetails.selectedMeals.addons.map((addon, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b border-purple-100 last:border-0">
                  <span className="text-sm font-medium text-purple-800">{addon.mealName}</span>
                  <span className="text-sm font-bold text-purple-900">AED {addon.fareDetails?.totalFare}</span>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Footer Summary */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2 text-gray-600">
            <span>Subtotal</span>
            <span>AED {grandTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center text-xl font-bold text-gray-900">
            <span>Total Amount</span>
            <span>AED {grandTotal.toFixed(2)}</span>
          </div>
        </div>

      </div>
    </div>
  );
};

// Extension Modal
const ExtensionDetailModal = ({ isOpen, onClose, extensionDetails }) => {
  if (!isOpen || !extensionDetails) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border-l-4 border-orange-500">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-bold text-gray-800 flex items-center">
            <Clock className="text-orange-500 mr-2" /> Plan Extension
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XCircle size={24} /></button>
        </div>

        <div className="space-y-4">
          <div className="flex justify-between p-3 bg-orange-50 rounded-lg">
            <span className="text-orange-800 font-medium">Days Added</span>
            <span className="text-orange-900 font-bold text-lg">{extensionDetails.extendedDays}</span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Original End</p>
              <p className="font-medium">{formatDate(extensionDetails.initialDate)}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">New End Date</p>
              <p className="font-bold text-green-600">{formatDate(extensionDetails.extendedTo)}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium">Close</button>
        </div>
      </div>
    </div>
  );
};

// Security Confirmation Modal
const SecurityConfirmModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const [password, setPassword] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm(password);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm border border-gray-200 animate-fadeIn">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Lock className="text-blue-600 mr-2" size={20} />
            Security Check
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle size={24} />
          </button>
        </div>

        <p className="text-gray-600 mb-4 text-sm">
          Please enter your login password to confirm this action.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Enter password..."
              autoFocus
              required
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center"
              disabled={loading}
            >
              {loading ? <Clock size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />}
              Confirm
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// --- Main Component ---

export default function OrderDetailsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [selectedExtensionDetails, setSelectedExtensionDetails] = useState(null);
  const [orderStatus, setOrderStatus] = useState('all');

  // Security Modal State
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [pendingActionOrderId, setPendingActionOrderId] = useState(null);
  const [verifyingSecurity, setVerifyingSecurity] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingOrderId, setLoadingOrderId] = useState(null);

  // Filter States for backend queries
  const [filterType, setFilterType] = useState('all'); // 'all' | 'date' | 'month' | 'year'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType === 'date') {
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      } else if (filterType === 'month') {
        if (selectedMonth) params.month = selectedMonth;
        if (selectedYear) params.year = selectedYear;
      } else if (filterType === 'year') {
        if (selectedYear) params.year = selectedYear;
      }

      const response = await axios.get('https://api.dailyfit.ae/api/admin/get-orders', {
        params,
        withCredentials: true
      });
      setOrders(response.data.data);
      console.log('Fetched orders with filters:', params, response.data.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

  const fetchOrderDetails = async (orderId) => {
    setLoadingOrderId(orderId);
    try {
      const response = await axios.post('https://api.dailyfit.ae/api/admin/get-orderDetails', { orderID: orderId }, { withCredentials: true });
      if (response.data.status && response.data.data.length > 0) {
        setOrderDetails(response.data.data[0]);
        setShowDetailsModal(true);
      }
    } catch (error) {
      alert('Failed to fetch order details.');
    } finally {
      setLoadingOrderId(null);
    }
  };

  // Initiates the payment update process (opens modal)
  const handleUpdatePaymentStatus = (orderId) => {
    setPendingActionOrderId(orderId);
    setShowSecurityModal(true);
  };

  const adminInfo = useSelector(selectAdmin);

  // Verifies password and executes the update
  const handleSecurityConfirm = async (password) => {
    if (!password) {
      toast.error("Password is required");
      return;
    }

    // 1. Get current admin email (Try Redux first, then fallback to LocalStorage)
    console.log("Debug: Checked Redux adminInfo:", adminInfo);
    let adminEmail = adminInfo?.email || adminInfo?.data?.email;

    // Fallback: Check localStorage if Redux failed
    if (!adminEmail) {
      const storageStr = localStorage.getItem('userInfo');
      console.log("Debug: Checked LocalStorage userInfo:", storageStr);
      if (storageStr) {
        try {
          const storage = JSON.parse(storageStr);
          adminEmail = storage.email || storage.data?.email;
        } catch (e) {
          console.error("Error parsing localStorage:", e);
        }
      }
    }

    if (!adminEmail) {
      toast.error("Session missing. Please logout and login again.");
      return;
    }

    const emailToSend = adminEmail.trim();
    console.log("Verifying credentials for:", emailToSend);

    setVerifyingSecurity(true);

    try {
      // 2. Verify Credentials using ADMIN endpoint
      // NOTE: Admin API expects 'username' instead of 'email'
      const verifyResponse = await axios.post('https://api.dailyfit.ae/api/admin/login', {
        username: emailToSend,
        password: password
      }, { withCredentials: true });

      console.log("Verification response:", verifyResponse.data);

      if (verifyResponse.data && verifyResponse.data.status === true) {
        // 3. Credentials Valid -> Proceed with Action
        await executePaymentUpdate(pendingActionOrderId);
        setShowSecurityModal(false);
        setPendingActionOrderId(null);
      } else {
        toast.error(verifyResponse.data.message || "Invalid password or email.");
      }
    } catch (error) {
      console.error("Security verification failed:", error);
      if (error.response) {
        console.error("Error Response Data:", error.response.data);
        console.error("Error Response Status:", error.response.status);
        toast.error(`Verification failed: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        console.error("No response received:", error.request);
        toast.error("Verification failed: No response from server. Check network.");
      } else {
        toast.error(`Verification failed: ${error.message}`);
      }
    } finally {
      setVerifyingSecurity(false);
    }
  };

  const executePaymentUpdate = async (orderId) => {
    setLoadingOrderId(orderId);
    try {
      const response = await axios.patch('https://api.dailyfit.ae/api/admin/update-payment-status', {
        orderID: orderId, paymentStatus: 2, paymentMethod: "Cash"
      }, { withCredentials: true });

      if (response.data.status) {
        toast.success("Payment status updated to Paid (Cash)!");
        fetchOrders();
      } else {
        toast.error(response.data.message || "Update failed");
      }
    } catch (error) {
      toast.error("Update failed.");
    } finally {
      setLoadingOrderId(null);
    }
  };

  // Function to open Google Maps (Updated from openGoogleMaps in original)
  const openGoogleMaps = (address) => {
    if (!address || !address.latitude || !address.longitude) {
      alert('Location coordinates not available for this address');
      return;
    }
    const lat = address.latitude;
    const lng = address.longitude;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const formatAddress = (address) => {
    if (!address) return 'No address';
    const clean = (val) => val && val !== 'undefined' && val !== 'null' ? val : null;
    const parts = [
      clean(address.flatNumber),
      clean(address.buildingName),
      clean(address.streetName),
      clean(address.landmark),
      clean(address.city)
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'No address details';
  };

  const filteredOrders = useMemo(() => {
    return orders.filter(order =>
      (searchTerm === '' ||
        order.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.orderID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.address?.phoneNumber?.includes(searchTerm))
      ) &&
      (orderStatus === 'all' ||
        (orderStatus === 'completed' && order.paymentStatus === 2) ||
        (orderStatus === 'pending' && order.paymentStatus === 1) ||
        (orderStatus === 'not-paid' && order.paymentStatus === 0)
      )
    );
  }, [orders, searchTerm, orderStatus]);

  const stats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter(o => o.paymentStatus === 1).length;
    const completed = orders.filter(o => o.paymentStatus === 2).length;
    const revenue = orders.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);
    return { total, pending, completed, revenue };
  }, [orders]);


  const columns = [
    {
      name: 'Order ID',
      selector: row => row.orderID,
      cell: row => <span className="text-gray-900 font-medium">{row.orderID}</span>, // Removed bold, added medium
      sortable: true,
      width: '150px',
    },
    {
      name: 'Email',
      selector: row => row.userEmail,
      cell: row => <span className="text-gray-700 break-words">{row.userEmail}</span>, // Lighter text color
      sortable: true,
      width: '200px',
    },
    {
      name: 'Address',
      cell: row => (
        <div className="text-sm text-gray-600 truncate" title={formatAddress(row.address)}>
          {formatAddress(row.address)}
        </div>
      ),
      width: '250px',
    },
    {
      name: 'Phone',
      selector: row => row.address?.phoneNumber || 'N/A',
      sortable: true,
      width: '140px',
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      cell: row => <span className="text-gray-800 font-medium">AED {row.amount}</span>, // Removed bold
      width: '120px',
    },
    {
      name: 'Status',
      sortable: true,
      sortFunction: (a, b) => a.paymentStatus - b.paymentStatus,
      cell: row => {
        const statuses = {
          0: { label: 'Unpaid', class: 'bg-red-50 text-red-600 border border-red-100', icon: XCircle },
          1: { label: 'Pending', class: 'bg-yellow-50 text-yellow-600 border border-yellow-100', icon: Clock },
          2: { label: 'Paid', class: 'bg-green-50 text-green-600 border border-green-100', icon: CheckCircle },
        };
        const st = statuses[row.paymentStatus] || statuses[0];
        const Icon = st.icon;
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center ${st.class}`}>
            <Icon size={12} className="mr-1" /> {st.label}
          </span>
        );
      },
      width: '130px',
    },
    {
      name: 'Location',
      cell: row => {
        const hasLocation = row.address?.latitude && row.address?.longitude;
        return (
          <button
            onClick={() => openGoogleMaps(row.address)}
            disabled={!hasLocation}
            className={`flex items-center px-3 py-1 rounded-md text-sm transition-colors ${hasLocation
              ? 'text-green-600 hover:text-green-800 hover:bg-green-50'
              : 'text-gray-400 cursor-not-allowed'
              }`}
            title={hasLocation ? 'View location on Google Maps' : 'Location not available'}
          >
            <MapPin size={16} className="mr-1" />
            {hasLocation ? 'View Map' : 'N/A'}
          </button>
        );
      },
      sortable: false,
      width: '120px',
    },
    {
      name: 'View Details',
      cell: row => (
        <button
          onClick={() => fetchOrderDetails(row.orderID)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
          title="View Details"
        >
          {loadingOrderId === row.orderID ? <Clock size={16} className="animate-spin mr-1" /> : <Info size={16} className="mr-1" />}
          View
        </button>
      ),
      width: '120px',
    },
    {
      name: 'Extension',
      cell: row => (
        row.extendedDetails?.isExtended ? (
          <button
            onClick={() => { setSelectedExtensionDetails(row.extendedDetails); setShowExtensionModal(true); }}
            className="px-3 py-1 bg-orange-50 text-orange-600 rounded-full text-xs font-medium hover:bg-orange-100 transition-colors flex items-center"
            title="View Extension"
          >
            <ArrowRightCircle size={14} className="mr-1" />
            View Ext
          </button>
        ) : (
          <span className="text-gray-400 text-xs italic">No Extension</span>
        )
      ),
      width: '130px',
    },
    {
      name: 'Payment Method',
      selector: row => row.paymentMethod,
      cell: row => (
        row.paymentMethod ? (
          <span className={`flex items-center text-xs font-medium px-2 py-1 rounded-md ${row.paymentMethod.toLowerCase() === 'cash' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
            }`}>
            {row.paymentMethod.toLowerCase() === 'cash' ? <Banknote size={14} className="mr-1" /> : <CreditCard size={14} className="mr-1" />}
            {row.paymentMethod}
          </span>
        ) : (
          <span className="text-gray-400 text-xs italic">Pending</span>
        )
      ),
      width: '150px',
    },
    {
      name: 'Payment',
      cell: row => (
        row.paymentStatus !== 2 ? (
          <button
            onClick={() => handleUpdatePaymentStatus(row.orderID)}
            className="px-3 py-1 bg-green-50 text-green-700 rounded-md text-xs font-medium hover:bg-green-100 transition-colors border border-green-200"
            title="Mark Cash Paid"
          >
            Mark Paid (AED)
          </button>
        ) : (
          <span className="text-green-600 text-xs font-medium flex items-center">
            <CheckCircle size={14} className="mr-1" /> Paid
          </span>
        )
      ),
      width: '140px',
      right: true
    }
  ];

  const customStyles = {
    table: { style: { backgroundColor: 'transparent' } },
    headRow: {
      style: {
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        color: '#6b7280', // Lighter header text
        fontWeight: '600',
        fontSize: '0.875rem',
        minHeight: '50px',
      },
    },
    rows: {
      style: {
        fontSize: '0.875rem',
        color: '#374151',
        backgroundColor: 'white',
        borderBottom: '1px solid #f3f4f6',
        minHeight: '60px', // Increased row height
        '&:hover': {
          backgroundColor: '#f8fafc',
          cursor: 'pointer',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          transition: 'all 0.2s',
        },
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #e5e7eb',
      }
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Orders Overview</h1>
        <p className="text-gray-500 mt-1">Manage and track all customer orders.</p>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Orders"
          value={stats.total}
          icon={Package}
          color={{ bg: 'bg-blue-100', text: 'text-blue-600' }}
        />
        <StatsCard
          title="Pending Payment"
          value={stats.pending}
          icon={Clock}
          color={{ bg: 'bg-yellow-100', text: 'text-yellow-600' }}
        />
        <StatsCard
          title="Completed"
          value={stats.completed}
          icon={CheckCircle}
          color={{ bg: 'bg-green-100', text: 'text-green-600' }}
        />
        <StatsCard
          title="Total Revenue"
          value={`AED ${stats.revenue.toLocaleString()}`}
          icon={TrendingUp}
          color={{ bg: 'bg-purple-100', text: 'text-purple-600' }}
          subValue="+12% from last week"
        />
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">

        {/* Toolbar */}
        <div className="p-5 border-b border-gray-100 bg-white flex flex-col space-y-4">
          
          {/* Row 1: Search & Status Filters */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search orders..."
                className="pl-10 pr-4 py-2.5 w-full border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-sm transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex items-center space-x-3 w-full md:w-auto justify-end">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment:</span>
              <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
                {['all', 'pending', 'completed'].map(status => (
                  <button
                    key={status}
                    onClick={() => setOrderStatus(status)}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${orderStatus === status ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 2: API Fetch Filters (Date / Month / Year) */}
          <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100">
            <div className="flex flex-col">
              <label className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Filter Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Orders</option>
                <option value="date">Date / Date Range</option>
                <option value="month">Month-wise</option>
                <option value="year">Year-wise</option>
              </select>
            </div>

            {filterType === 'date' && (
              <>
                <div className="flex flex-col">
                  <label className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">End Date (Optional)</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            {filterType === 'month' && (
              <>
                <div className="flex flex-col">
                  <label className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {[
                      { val: '1', name: 'January' },
                      { val: '2', name: 'February' },
                      { val: '3', name: 'March' },
                      { val: '4', name: 'April' },
                      { val: '5', name: 'May' },
                      { val: '6', name: 'June' },
                      { val: '7', name: 'July' },
                      { val: '8', name: 'August' },
                      { val: '9', name: 'September' },
                      { val: '10', name: 'October' },
                      { val: '11', name: 'November' },
                      { val: '12', name: 'December' },
                    ].map(m => (
                      <option key={m.val} value={m.val}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col">
                  <label className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Year</label>
                  <input
                    type="number"
                    min="2020"
                    max="2050"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="px-3 py-2 w-24 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            {filterType === 'year' && (
              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Year</label>
                <input
                  type="number"
                  min="2020"
                  max="2050"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-3 py-2 w-24 border border-gray-200 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {/* Clear Button */}
            {(filterType !== 'all' || startDate || endDate) && (
              <button
                onClick={() => {
                  setFilterType('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="mt-5 px-4 py-2 text-sm font-semibold text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-all"
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="h-64 flex flex-col items-center justify-center text-gray-400">
              <div className="animate-spin mb-3"><Clock size={32} className="text-blue-500" /></div>
              <p>Loading orders...</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredOrders}
              pagination
              customStyles={customStyles}
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 50]}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 250px)"
              highlightOnHover
              pointerOnHover
              noDataComponent={
                <div className="p-10 text-center flex flex-col items-center text-gray-400">
                  <ShoppingBag size={48} className="mb-3 opacity-20" />
                  <p>No orders found matching your criteria.</p>
                </div>
              }
            />
          )}
        </div>
      </div>

      <MealDetailModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        orderDetails={orderDetails}
      />

      <ExtensionDetailModal
        isOpen={showExtensionModal}
        onClose={() => setShowExtensionModal(false)}
        extensionDetails={selectedExtensionDetails}
      />

      {/* Security Confirmation Modal */}
      <SecurityConfirmModal
        isOpen={showSecurityModal}
        onClose={() => { setShowSecurityModal(false); setPendingActionOrderId(null); }}
        onConfirm={handleSecurityConfirm}
        loading={verifyingSecurity}
      />

      <ToastContainer position="bottom-right" theme="colored" />
    </div>
  );
}